import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Button, Card, Col, DatePicker, Input, Layout, message, Modal, Row, Select, Table } from 'antd';
import moment from 'moment';
import { ExportOutlined, FileExcelOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import Axios from 'axios';
import { url } from '../../config/global';
import fileDownload from 'js-file-download';
import TableSPStockSupplier from './TableSPStockSupplier';
import UploadSPStockSupplier from './UploadSPStockSupplier';
import DetailSPStockSupplier from './DetailSPStockSupplier';
import DetailAdjustSPStockSupplier from './DetailAdjustSPStockSupplier';
import { Redirect } from 'react-router-dom';
import ErrorSPStockSupplier from './ErrorSPStockSupplier';

const { Content } = Layout;
const { Option } = Select;

const SPStockSupplier = () => {
  const [inputKeyword, setInputKeyword] = useState('');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [isExport, setIsExport] = useState(false);
  const [page, setPage] = useState(1);
  const [failedData, setFailedData] = useState([]);
  const [failedModal, setFailedModal] = useState(0);
  const [pageSize, setPagesize] = useState(10);
  const [buttonAccess, setButtonAccess] = useState(null);
  const [isModalUpload, setIsModalUpload] = useState(false);
  const [data, setData] = useState([]);
  const [mainSort, setMainSort] = useState({
    sort: 'created_at',
    order: 'descend',
  });
  const [supplierList, setSupplierList] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [userRole, setUserRole] = useState('');
  const [isModalDetail, setIsModalDetail] = useState(false);
  const [isModalDetailAdjust, setIsModalDetailAdjust] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const getData = useCallback(
    async (searchParams, currentPage = 1, sort = mainSort) => {
      if (!searchParams?.supplier && userRole !== 'admin') {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await Axios.get(`${url}/stock-inventory`, {
          params: {
            ...searchParams,
            perpage: pageSize,
            page: currentPage,
            sort: sort.sort,
            order: sort.order,
          },
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`,
          },
        });
        setData(res.data);
      } catch (err) {
        if (err.response) {
          message.error(err.response.data.message);
          if (err.response.status === 401) {
            sessionStorage.removeItem('token');
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [pageSize, mainSort, userRole]
  );

  const initializeData = useCallback(async () => {
    setLoading(true);
    try {
      const permissionsRaw = sessionStorage.getItem('permissions');
      let permissions = [];

      try {
        permissions = JSON.parse(permissionsRaw);
      } catch (e) {
        console.error('Failed to parse permissions', e);
      }

      const role = permissions.some((p) => p.name.toLowerCase().includes('master')) ? 'admin' : 'supplier';
      setUserRole(role);

      const response = await Axios.get(`${url}/stock-inventory/getSupplier`, {
        params: { keyword: '' },
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
      });

      const supplierData = response.data.data;
      let defaultSupplier = null;
      let filteredSuppliers = supplierData;

      if (role === 'supplier') {
        const userSupplierId = sessionStorage.getItem('id_user');

        if (userSupplierId) {
          const eksternalUserRes = await Axios.get(`${url}/stock-inventory/getDetailSupplier/${userSupplierId}`, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token')}`,
            },
          });
          const eksternalUserData = eksternalUserRes.data?.data;
          const eksternalSupplierName = eksternalUserData?.supplier_name;

          const filteredSupplier = supplierData.find((s) => s.supplier_name.includes(eksternalSupplierName));

          if (filteredSupplier) {
            filteredSuppliers = [filteredSupplier];
            defaultSupplier = {
              code: filteredSupplier.supplier_code,
              name: filteredSupplier.supplier_name,
            };
          } else {
            filteredSuppliers = [];
          }
        }
      }

      setSupplierList(filteredSuppliers);
      setSelectedSupplier(defaultSupplier);

      const initialSearchParams = {
        before_item: '',
        supplier: defaultSupplier?.name || '',
        year: selectedMonth ? selectedMonth.format('YYYY') : null,
        month: selectedMonth ? selectedMonth.format('MM') : null,
      };

      setKeyword(initialSearchParams);

      if (defaultSupplier || role === 'admin') {
        await getData(initialSearchParams, 1, mainSort);
      }

      setInitialized(true);
    } catch (err) {
      if (err.response) {
        message.error(err.response.data.message);
        if (err.response.status === 401) {
          sessionStorage.removeItem('token');
        }
      }
      setInitialized(true);
    } finally {
      setLoading(false);
    }
  }, [getData, mainSort, selectedMonth]);

  useEffect(() => {
    if (!initialized) {
      initializeData();
    }

    const pathname = window.location.pathname.split('/');
    const currentUrl = pathname[1];
    const permissions = sessionStorage.getItem('permissions') && JSON.parse(sessionStorage.getItem('permissions'));

    if (permissions) {
      const findPermission = permissions.find((permission) => permission.url === currentUrl);
      const findChildPermission = permissions
        .map((child) => {
          return child.children.find((grandChild) => grandChild.url === currentUrl);
        })
        .filter((grandChild) => grandChild)[0];

      if (findChildPermission && findChildPermission.children) {
        const findButtonAccessChildren = findChildPermission.children.filter((x) => x !== null).filter((x) => x.permission_type === 'button');
        if (findButtonAccessChildren) {
          setButtonAccess(findButtonAccessChildren.filter((x) => x !== null));
        }
      }

      if (findPermission && findPermission.children) {
        const findButtonAccess = findPermission.children.filter((x) => x.permission_type === 'button');
        if (findButtonAccess) {
          setButtonAccess(findButtonAccess);
        }
      }
    }
  }, [initialized, initializeData]);

  useEffect(() => {
    if (initialized && keyword && (keyword.supplier || userRole === 'admin')) {
      getData(keyword, page, mainSort);
    }
  }, [page, pageSize, mainSort, initialized, keyword, userRole, getData]);

  const showModal = () => {
    setIsModalUpload(true);
  };

  const closeModal = () => {
    setData({
      ...data,
      file: null,
    });
    setIsModalUpload(false);
    setFailedModal(false);
  };

  const showModalDetail = (record) => {
    setSelectedRow(record);
    setIsModalDetail(true);
  };

  const closeModalDetail = () => {
    setSelectedRow(null);
    setIsModalDetail(false);
  };

  const showModalDetailAdjust = (record) => {
    setSelectedRow(record);
    setIsModalDetailAdjust(true);
  };

  const closeModalDetailAdjust = () => {
    setSelectedRow(null);
    setIsModalDetailAdjust(false);
  };

  const downloadTemplate = () => {
    Axios.get(`${url}/stock-inventory/template`, {
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
      },
      responseType: 'blob',
    }).then((res) => {
      fileDownload(res.data, 'Template Stock Inventory Supplier.xlsx');
    });
  };

  const handleExport = () => {
    setIsExport(true);

    Axios.get(`${url}/stock-inventory/export`, {
      params: {
        ...keyword,
      },
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
      },
      responseType: 'blob',
    })
      .then((res) => {
        const year = keyword.year || 'yyyy';
        const month = keyword.month || 'mm';

        const filename = `Stock Inventory Supplier - ${month}-${year}.xlsx`;
        fileDownload(res.data, filename);
      })
      .catch((err) => {
        console.error('Export error:', err);
      })
      .finally(() => {
        setIsExport(false);
      });
  };

  const handleSearch = () => {
    const searchParams = {
      before_item: inputKeyword,
      supplier: selectedSupplier?.name || '',
      year: selectedMonth ? selectedMonth.format('YYYY') : null,
      month: selectedMonth ? selectedMonth.format('MM') : null,
    };
    setPage(1);
    setKeyword(searchParams);
  };

  if (!sessionStorage.getItem('token')) {
    return <Redirect to="/login" />;
  }

  return (
    <Fragment>
      <ErrorSPStockSupplier failedModal={failedModal} closeModal={closeModal} loading={loading} failedData={failedData} setPage={setPage} setPagesize={setPagesize} setMainSort={setMainSort} />

      <UploadSPStockSupplier isVisible={isModalUpload} closeModal={closeModal} getData={() => getData(keyword, page, mainSort)} setFailedData={setFailedData} setFailedModal={setFailedModal} />

      <DetailSPStockSupplier isVisible={isModalDetail} onCancel={closeModalDetail} record={selectedRow} month={selectedMonth} pageSize={pageSize} token={sessionStorage.getItem('token')} />

      <DetailAdjustSPStockSupplier
        isVisible={isModalDetailAdjust}
        onCancel={closeModalDetailAdjust}
        record={selectedRow}
        pageSize={pageSize}
        token={sessionStorage.getItem('token')}
        selectedMonth={selectedMonth}
        userRole={userRole}
        selectedSupplier={selectedSupplier}
        url={url}
      />

      <Row>
        <Col xs={24}>
          <Card type="inner" title="Stock Inventory Supplier" bodyStyle={{ padding: '0' }} extra={moment(new Date()).format('DD MMMM YYYY')} />
        </Col>
      </Row>

      <Content className="content">
        <Row>
          <Col xs={24}>
            <Card className="body-data">
              <Row type="flex" gutter={[10, 20]} justify="space-between">
                <Col xs={24} md={12} lg={12}>
                  <Row gutter={[8, 8]}>
                    {buttonAccess?.find((x) => x.url === 'stock-inventory/import') && (
                      <Col xs={12} sm={8} md={6} lg={6}>
                        <Button onClick={showModal} type="primary" style={{ width: '100%' }}>
                          <UploadOutlined />
                          Adjust Stock
                        </Button>
                      </Col>
                    )}
                    {buttonAccess?.find((x) => x.url === 'stock-inventory/export') && (
                      <Col xs={12} sm={8} md={6} lg={6}>
                        <Button loading={isExport} onClick={handleExport} type="primary" style={{ width: '100%' }}>
                          <ExportOutlined />
                          Export
                        </Button>
                      </Col>
                    )}
                    {buttonAccess?.find((x) => x.url === 'stock-inventory/template') && (
                      <Col xs={12} sm={8} md={6} lg={6}>
                        <Button type="default" onClick={downloadTemplate} style={{ width: '100%' }}>
                          <FileExcelOutlined />
                          Template
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>

                <Col xs={24} md={12} lg={12}>
                  <Row gutter={[8, 8]} justify="end">
                    <Col xs={24} sm={8} md={6} lg={6}>
                      <Input placeholder="Search Item Number" value={inputKeyword} onChange={(e) => setInputKeyword(e.target.value)} onPressEnter={handleSearch} />
                    </Col>
                    <Col xs={24} sm={12} md={10} lg={10}>
                      <Select
                        placeholder="Select Supplier"
                        style={{ width: '100%' }}
                        value={selectedSupplier ? JSON.stringify(selectedSupplier) : undefined}
                        onChange={(value) => {
                          if (value === undefined) {
                            setSelectedSupplier(null);
                          } else {
                            setSelectedSupplier(JSON.parse(value));
                          }
                        }}
                        disabled={userRole !== 'admin'}
                        optionFilterProp="children"
                        allowClear
                        showSearch
                      >
                        {supplierList &&
                          supplierList.map((supplier) => (
                            <Option value={JSON.stringify({ code: supplier.supplier_code, name: supplier.supplier_name })} key={supplier._id}>
                              {`${supplier.supplier_code} - ${supplier.supplier_name}`}
                            </Option>
                          ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={6}>
                      <DatePicker placeholder="Pilih Bulan" picker="month" value={selectedMonth} onChange={(date) => setSelectedMonth(date)} style={{ width: '100%' }} />
                    </Col>
                    <Col xs={24} sm={12} md={2} lg={2}>
                      <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} style={{ width: '100%' }} />
                    </Col>
                  </Row>
                </Col>

                <Col xs={24}>
                  <TableSPStockSupplier
                    data={data}
                    loading={loading}
                    page={page}
                    pageSize={pageSize}
                    setPage={setPage}
                    setPagesize={setPagesize}
                    setMainSort={setMainSort}
                    showModalDetail={showModalDetail}
                    showModalDetailAdjust={showModalDetailAdjust}
                    isMobile={isMobile}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Content>
    </Fragment>
  );
};

export default SPStockSupplier;
